import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Category, Subcategory } from '@/types/category';
import { Product } from '@/types/product';
import { fetchAllSubcategories } from '@/services/categoryService';
import { fetchFeaturedProducts, fetchProducts } from '@/services/productService';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/context/LanguageContext';

const categorySlug = (name: string) => name.toLowerCase().replace(/\s+/g, '-');

const productImage = (product: Product) =>
  product.images?.[0] || product.image_nobg || '';

/** Title-case without forcing mid-word caps (e.g. "Day systems") */
const toTitleCase = (value: string) =>
  value
    .toLowerCase()
    .replace(/(^|\s|-)\S/g, (match) => match.toUpperCase());

interface ProductsMegaMenuProps {
  categories: Category[];
  triggerClassName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  headerOffset: number;
}

const FeaturedRotator: React.FC<{ products: Product[]; active: boolean }> = ({
  products,
  active,
}) => {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (!active || products.length < 2) return;

    const interval = window.setInterval(() => {
      setVisible(false);
      window.setTimeout(() => {
        setIndex((prev) => {
          let next = Math.floor(Math.random() * products.length);
          if (next === prev) next = (prev + 1) % products.length;
          return next;
        });
        setVisible(true);
      }, 350);
    }, 4000);

    return () => window.clearInterval(interval);
  }, [active, products]);

  if (!products.length) return null;

  const product = products[index] ?? products[0];
  const image = productImage(product);
  if (!image) return null;

  return (
    <Link
      to={`/product/${product.id}`}
      className="hidden w-[260px] shrink-0 self-start lg:block xl:w-[300px]"
    >
      <div className="relative aspect-square w-full overflow-hidden bg-neutral-100">
        <img
          key={`${product.id}-${index}`}
          src={image}
          alt={product.name}
          className={cn(
            'absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ease-in-out',
            visible ? 'opacity-100' : 'opacity-0'
          )}
        />
      </div>
      <p
        className={cn(
          'mt-3 text-[11px] font-normal uppercase tracking-[0.16em] text-neutral-400 transition-opacity duration-500',
          visible ? 'opacity-100' : 'opacity-0'
        )}
      >
        {product.name}
      </p>
    </Link>
  );
};

const ProductsMegaMenu: React.FC<ProductsMegaMenuProps> = ({
  categories,
  triggerClassName,
  open,
  onOpenChange,
  headerOffset,
}) => {
  const { t } = useLanguage();
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [featured, setFeatured] = useState<Product[]>([]);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [subs, featuredList] = await Promise.all([
          fetchAllSubcategories(),
          fetchFeaturedProducts(),
        ]);
        if (cancelled) return;
        setSubcategories(subs);

        let pool = featuredList.filter((p) => productImage(p));
        if (pool.length < 3) {
          const more = await fetchProducts({ limit: 16 });
          pool = [...pool, ...more.filter((p) => productImage(p))];
        }

        const seen = new Set<string>();
        const unique = pool.filter((p) => {
          if (seen.has(p.id)) return false;
          seen.add(p.id);
          return true;
        });

        // Light shuffle
        for (let i = unique.length - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          [unique[i], unique[j]] = [unique[j], unique[i]];
        }

        setFeatured(unique.slice(0, 12));
      } catch (error) {
        console.error('Error loading mega menu data:', error);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const grouped = useMemo(() => {
    const byCategory = new Map<string, Subcategory[]>();
    subcategories.forEach((sub) => {
      const list = byCategory.get(sub.category_id) || [];
      list.push(sub);
      byCategory.set(sub.category_id, list);
    });
    return categories.map((category) => ({
      category,
      subs: byCategory.get(category.id) || [],
    }));
  }, [categories, subcategories]);

  // Pack category blocks into up to 4 columns (Poliform stacks groups per column)
  const columns = useMemo(() => {
    const colCount = Math.min(4, Math.max(1, grouped.length));
    const perCol = Math.ceil(grouped.length / colCount);
    return Array.from({ length: colCount }, (_, i) =>
      grouped.slice(i * perCol, (i + 1) * perCol)
    ).filter((col) => col.length > 0);
  }, [grouped]);

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const handleEnter = () => {
    clearCloseTimer();
    onOpenChange(true);
  };

  const handleLeave = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => onOpenChange(false), 140);
  };

  useEffect(() => () => clearCloseTimer(), []);

  return (
    <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        type="button"
        className={triggerClassName}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => onOpenChange(!open)}
      >
        {t('header.products')}
      </button>

      {open && (
        <div
          className="fixed inset-x-0 z-[60] border-b border-neutral-200 bg-white animate-in fade-in-0 slide-in-from-top-1 duration-200"
          style={{ top: headerOffset }}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          <div className="mx-auto flex w-full max-w-[1400px] items-start gap-12 px-8 py-12 md:px-12 lg:gap-16 lg:px-16 lg:py-14">
            <div className="grid min-w-0 flex-1 grid-cols-2 gap-x-10 gap-y-10 sm:grid-cols-3 xl:grid-cols-4 xl:gap-x-14">
              {columns.map((column, colIndex) => (
                <div key={colIndex} className="flex flex-col gap-10">
                  {column.map(({ category, subs }) => (
                    <div key={category.id}>
                      <Link
                        to={`/category/${categorySlug(category.name)}`}
                        className="mb-3 block text-[12px] font-bold uppercase tracking-[0.14em] text-black transition-opacity hover:opacity-50"
                        onClick={() => onOpenChange(false)}
                      >
                        {category.name}
                      </Link>
                      {subs.length > 0 && (
                        <ul className="space-y-2.5">
                          {subs.map((sub) => (
                            <li key={sub.id}>
                              <Link
                                to={`/category/${categorySlug(category.name)}`}
                                className="block text-[13px] font-normal leading-snug text-neutral-600 transition-colors hover:text-black"
                                onClick={() => onOpenChange(false)}
                              >
                                {toTitleCase(sub.name)}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <FeaturedRotator products={featured} active={open} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsMegaMenu;
